import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { registerDataValidationErrorMatcher } from 'errors/dataValidationError';
import useKeyDownListener from 'hooks/useKeyDownListener';
import FolderPlusSvg from 'images/v5/icons/folder-plus.svg';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { addFolder, updateFolder } from 'store/specificationsFoldersSlice';
import useFolderValidation from './useFolderValidation';
function FolderCreator({ parentFolder, edit, onClose, }) {
    var _a, _b;
    const { t } = useTranslation(['specification', 'common', 'exceptions']);
    const dispatch = useDispatch();
    const folderData = (_a = useSelector(selectModalByName(edit ? 'folderEditor' : 'folderCreator'))) === null || _a === void 0 ? void 0 : _a.data;
    const [folderForm, setFolderForm] = useState({ name: '' });
    const [serverError, setServerError] = useState(null);
    const statusAdd = useSelector((state) => state.specificationsFolders.status.add);
    const statusUpdate = useSelector((state) => state.specificationsFolders.status.update);
    const { errors, isValid, pendingFields, markTouched, validateAll } = useFolderValidation(folderForm, t);
    useEffect(() => {
        if (edit) {
            setFolderForm({ name: folderData.name });
        }
    }, [edit, folderData === null || folderData === void 0 ? void 0 : folderData.name]);
    const handleFormChange = ({ target: { name, value }, }) => {
        setFolderForm((folder) => (Object.assign(Object.assign({}, folder), { [name]: value })));
        setServerError(null);
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: edit ? 'folderEditor' : 'folderCreator' }));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        setServerError(null);
        if (edit) {
            dispatch(updateFolder(Object.assign(Object.assign({}, folderForm), { modelSpecificationFolderId: folderData.id })))
                .unwrap()
                .then(() => dispatch(closeModal({ name: 'folderEditor' })))
                .catch((exceptionCode) => setServerError(t(`exceptions:${exceptionCode}`)));
        }
        else if (parentFolder) {
            dispatch(addFolder(Object.assign(Object.assign({}, folderForm), { parentFolderId: parentFolder })))
                .unwrap()
                .then(() => dispatch(closeModal({ name: 'folderCreator' })))
                .catch((exceptionCode) => setServerError(t(`exceptions:${exceptionCode}`)));
        }
    };
    registerDataValidationErrorMatcher();
    useKeyDownListener([
        { code: 'Enter', callback: handleFormSubmit },
        { code: 'Escape', callback: onClose },
    ]);
    return (React.createElement(Modal, { name: edit ? 'folderEditor' : 'folderCreator', size: "s", header: React.createElement(Label, { weight: "strong" },
            React.createElement(FolderPlusSvg, null),
            " ",
            edit ? t('edit_folder_name') : t('adding_new_folder')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: statusAdd === 'loading' || statusUpdate === 'loading', pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCancel, validateAll: validateAll }) },
        React.createElement(FormField, { label: t('folder_name'), error: (_b = errors.name) !== null && _b !== void 0 ? _b : serverError },
            React.createElement(Input, { value: folderForm.name, onChange: handleFormChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name) || Boolean(serverError), autoFocus: true }))));
}
export default memo(FolderCreator);
